<template>
  <div class="review">
    <h1>Detailed Feedback</h1>

    <!--   Go To Dashboard btn      -->
    <div class="row review2dashboard">
      <div class="col-md-12">
        <button @click="showDashboardClicked">Go back to the dashboard</button>
      </div>
    </div>

    <!--  Question Numbers table       -->
    <p @click="showNavigationTable = !showNavigationTable" class="nav-table">
      <span v-if="!showNavigationTable" class="cs-expand">+</span>
      <span v-if="showNavigationTable" class="cs-collapse">-</span>
      <span>Navigation Table</span>
    </p>
    <div v-if="showNavigationTable" class="question-numbers">
      <span v-for="(q, index) in questions" :key="index">
        <span
          :class="questionNumberClass(index)"
          @click="changeCurrentQuestion(index)"
          :key="index"
          v-text="index + 1 < 10 ? '0' + (index + 1) : index + 1"
        ></span>
      </span>
    </div>

    <!--   Questions and Choices     -->
    <ul class="questions">
      <li v-show="q_index === current_question_index" v-for="(question, q_index) in questions" :key="q_index">
        <div class="questionCode" v-text="question.question_code"></div>

        <div class="question-result">
          <div v-if="questionAnsweredCorrectly(question)" class="correct">
            <span>Answered Correctly</span>
          </div>
          <div v-else class="incorrect">
            <span>Answered Incorrectly</span>
          </div>
        </div>

        <div v-if="question.is_flagged" class="flag">Flagged</div>

        <div class="title" v-html="marked(q_index + 1 + '- ' + question.problem_title)"></div>

        <div class="choices">
          <div class="choice">
            <table>
              <tbody>
                <tr>
                  <td>Yours</td>
                  <td>Expected</td>
                  <td></td>
                </tr>
                <tr class="choice" v-for="(choice, c_index) in question.choices" :key="c_index">
                  <td
                    :class="calcChoiceClass(choice.is_correct, choice.is_chosen)"
                    v-text="choice.is_chosen ? '✓' : '✘'"
                  ></td>
                  <td v-text="choice.is_correct ? '✓' : '✘'"></td>
                  <td
                    class="choice-title"
                    v-html="marked(alphabets[c_index] + '. ' + choice.choice_title)"
                  ></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!--     Feedback           -->
        <div class="feedback mt-4" v-html="marked(question.question_feedback)"></div>
      </li>
    </ul>

    <!--  Next, Preview, Coaching Buttons    -->
    <div class="buttons">
      <div class="row">
        <div class="col-md-3">
          <button
            title="Previous incorrect answer or flagged question"
            @click="gotoPreviousFlaggedOrWrongQuestion"
            :class="{ disabled: previousWrongOrFlaggedQuestion === null }"
            :disabled="previousWrongOrFlaggedQuestion === null"
          >
            <prev></prev>
            <prev></prev>
          </button>
          <button @click="previous" :class="{ disabled: isFirstQuestion }" :disabled="isFirstQuestion">
            <prev></prev>&nbsp;Previous
          </button>
        </div>
        <div class="col-md-3">
          <button @click="next" :class="{ disabled: isLastQuestion }" :disabled="isLastQuestion">
            Next
            <next></next>
          </button>
          <button
            title="Next incorrect answer or flagged question"
            @click="gotoNextFlaggedOrWrongQuestion"
            :class="{ disabled: nextWrongOrFlaggedQuestion === null }"
            :disabled="nextWrongOrFlaggedQuestion === null"
          >
            <next></next>
            <next></next>
          </button>
        </div>
        <div class="col-md-3"></div>

        <div v-if="coaching_button_visible" class="col-md-3">
          <button v-if="coachingButtonVisible" @click="showCoachingForm">
            Coaching
          </button>
        </div>
      </div>
    </div>

    <!--  Coaching form      -->
    <div class="coaching-wrapper" v-if="coaching_form_visible">
      <p>
        You can use the following form to contact your coach and get help for this question. It takes about
        two days to receive the answer.
      </p>
      <p>
        Details of the question will be sent automatically and you don’t need to copy them into the form.
      </p>

      <div v-if="coachingEnabled">
        <textarea
          class="coaching-textarea"
          ref="user_query"
          cols="30"
          rows="5"
          placeholder="Your question"
        ></textarea>
      </div>
      <div v-else class="coaching-disabled-message">
        <div v-html="exam_details.coaching_disabled_message"></div>
      </div>

      <button @click="cancelCoaching">Cancel</button>
      <button
        class="submit-btn"
        :class="{ disabled: !coachingEnabled }"
        @click="submitCoaching"
        :disabled="!coachingEnabled"
      >
        Submit
      </button>
    </div>
  </div>
</template>

<script>
import Next from '../Common/NextIcon'
import Prev from '../Common/PrevIcon'
import { mapActions, mapState } from 'vuex'

export default {
  name: 'Review',
  data() {
    return {
      current_question_index: 0,
      coaching_form_visible: false,
      coaching_button_visible: true,
      showNavigationTable: false
    }
  },
  components: {
    Prev,
    Next
  },

  computed: {
    ...mapState('review', {
      questions: 'review_questions'
    }),
    ...mapState('exam', ['exam_details']),
    ...mapState(['user_id', 'user_email', 'alphabets', 'is_demo']),

    isLastQuestion() {
      return this.current_question_index === this.questions.length - 1
    },
    isFirstQuestion() {
      return this.current_question_index === 0
    },

    nextWrongOrFlaggedQuestion() {
      let result = null
      for (let i = 0; i <= this.questions.length - 1; i++) {
        if (i > this.current_question_index) {
          if (this.questions[i].is_flagged || !this.questionAnsweredCorrectly(this.questions[i])) {
            result = i
            break
          }
        }
      }
      return result
    },

    previousWrongOrFlaggedQuestion() {
      let result = null
      for (let i = this.questions.length - 1; i >= 0; i--) {
        if (i < this.current_question_index) {
          if (this.questions[i].is_flagged || !this.questionAnsweredCorrectly(this.questions[i])) {
            result = i
            break
          }
        }
      }
      return result
    },

    coachingButtonVisible() {
      if (
        this.questions[this.current_question_index].coaching_used !== true &&
        this.exam_details.coaching_available === 1
      ) {
        return true
      }

      return false
    },

    coachingEnabled() {
      // TODO: get coaching_enabled status for this user from
      // backend, probably exam_instance_table
      if (this.is_demo) {
        return false
      }
      return true
    }
  },
  methods: {
    ...mapActions(['showDashboardClicked']),

    calcChoiceClass(is_correct, is_chosen) {
      if (!(is_correct ^ is_chosen)) {
        return 'correct'
      } else {
        return 'incorrect'
      }
    },
    previous() {
      if (this.current_question_index > 0) this.current_question_index--
    },
    next() {
      if (this.current_question_index < this.questions.length - 1) this.current_question_index++
    },
    changeCurrentQuestion(index) {
      this.current_question_index = index
    },

    gotoNextFlaggedOrWrongQuestion() {
      this.current_question_index = this.nextWrongOrFlaggedQuestion
    },
    gotoPreviousFlaggedOrWrongQuestion() {
      this.current_question_index = this.previousWrongOrFlaggedQuestion
    },

    questionAnsweredCorrectly(question) {
      let result = true
      question.choices.forEach((choice) => {
        if (choice.is_correct ^ choice.is_chosen) {
          return (result = false)
        }
      })

      return result
    },

    // calculate css class for every question number
    questionNumberClass(index) {
      // default class
      let result = 'item'

      // calc active
      if (index === this.current_question_index) {
        result += ' active'
      }

      // calc flagged
      let q = this.questions[index]
      if (q.is_flagged) {
        result += ' flagged'
      }

      // calc correct or incorrect class
      if (this.questionAnsweredCorrectly(this.questions[index])) {
        result += ' correct'
      } else {
        result += ' incorrect'
      }

      // calc chosen
      let chosen_class = ''
      q.choices.forEach((choice) => {
        if (choice.is_chosen) {
          chosen_class = ' chosen'
        }
      })

      result += chosen_class
      return result
    },

    showCoachingForm() {
      this.coaching_form_visible = true
      this.coaching_button_visible = false
    },

    cancelCoaching() {
      this.coaching_form_visible = false
      this.coaching_button_visible = true
      this.$set(this.questions[this.current_question_index], 'coaching_used', false)
    },

    handleKeyDown(event) {
      const key = event.key // "ArrowRight", "ArrowLeft", "ArrowUp", or "ArrowDown"
      if (this.$refs.user_query === document.activeElement) {
        return
      }
      if (key === 'ArrowRight' || key === 'k' || key === 'd') {
        this.next()
      }
      if (key === 'ArrowLeft' || key === 'j' || key === 'a') {
        this.previous()
      }
      if ((key === 'h' || key === 'w') && this.previousWrongOrFlaggedQuestion) {
        this.gotoPreviousFlaggedOrWrongQuestion()
      }
      if ((key === 'l' || key === 's') && this.nextWrongOrFlaggedQuestion) {
        this.gotoNextFlaggedOrWrongQuestion()
      }
    },

    submitCoaching() {
      let data = {
        user_id: this.user_id,
        user_email: this.user_email,
        exam_title: this.exam_details.title,
        coaching_email: this.exam_details.coaching_email,
        question_code: this.questions[this.current_question_index].question_code,
        question_feedback: this.questions[this.current_question_index].question_feedback,
        question_number: this.current_question_index + 1,
        question_result: this.questionAnsweredCorrectly(this.questions[this.current_question_index])
          ? 'Answered Correctly!'
          : 'Answered Incorrectly!',
        question_title: this.questions[this.current_question_index].problem_title,
        user_query: this.$refs.user_query.value,
        choices: this.questions[this.current_question_index].choices,

        // just used in mutation
        current_question_index: this.current_question_index
      }

      this.$store.dispatch('review/submitCoaching', data)

      this.coaching_form_visible = false
    }
  },

  watch: {
    current_question_index() {
      this.coaching_form_visible = false
      this.coaching_button_visible = true
    }
  },

  mounted() {
    document.addEventListener('keydown', this.handleKeyDown)
  },
  destroyed() {
    document.removeEventListener('keydown', this.handleKeyDown)
  }
}
</script>
